import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Eyebrow, FadeUp, MaskedLine } from "@/components/motion";

const inputCls =
  "w-full rounded-xl border border-brass/20 bg-ink/70 px-4 py-3.5 text-base text-ivory placeholder:text-ivory/35 transition-colors focus:border-brass focus:outline-none";
const labelCls = "mb-2 block text-sm font-medium text-ivory/80";

const POSITIONS = ["Top", "Top / Vers", "Vers", "Vers / Bottom", "Bottom", "Side"];

const DealMeIn = () => {
  const [form, setForm] = useState({
    first_name: "",
    email: "",
    mobile: "",
    age: "",
    position: "",
    privacy_ack: false,
  });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name.trim() || !form.email.trim() || !form.mobile.trim() || !form.age) {
      setError("Please complete your first name, email, mobile and age.");
      return;
    }
    if (Number(form.age) < 18) {
      setError("Players Lounge is strictly 18+.");
      return;
    }
    if (!form.privacy_ack) {
      setError("Please acknowledge the privacy note to continue.");
      return;
    }
    setState("sending");
    try {
      await api.post("/deal-me-in", {
        first_name: form.first_name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        age: Number(form.age),
        position: form.position || null,
        privacy_ack: form.privacy_ack,
      });
      setState("done");
      window.scrollTo(0, 0);
    } catch (err) {
      setError(formatApiError(err));
      setState("idle");
    }
  };

  return (
    <div data-testid="deal-me-in-page">
      <section className="lamp-glow mx-auto max-w-7xl px-5 pb-14 pt-36 sm:px-8 sm:pt-44">
        <Eyebrow suit="♦">Join the Players List</Eyebrow>
        <h1 className="mt-4 font-serif text-5xl leading-[1.02] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.1}>Ready to be</MaskedLine>
          <MaskedLine delay={0.25} className="italic text-brass">
            dealt in?
          </MaskedLine>
        </h1>
        <FadeUp delay={0.4}>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ivory/70">
            Joining the list doesn't commit you to attending anything. It simply means you'll hear
            about upcoming Players Lounge gatherings and have the opportunity to book when a game
            appeals to you.
          </p>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-8 sm:pb-28">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr]">
          <FadeUp>
            <div className="space-y-6">
              <h2 className="font-serif text-2xl text-ivory sm:text-3xl">Before you apply</h2>
              <ul className="space-y-4 text-[15px] leading-relaxed text-ivory/70">
                <li className="flex gap-3">
                  <span aria-hidden="true" className="text-brass">♠</span>
                  We ask a few questions because they help us create a good mix of Players.
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="text-brass">♥</span>
                  Curation isn't about judging who's attractive. It's about creating a room that works.
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="text-brass">♦</span>
                  Applications are treated confidentially and never shared with other Players.
                </li>
                <li className="flex gap-3">
                  <span aria-hidden="true" className="text-brass">♣</span>
                  Players Lounge is strictly 18+.
                </li>
              </ul>
              <div className="rounded-2xl border border-brass/15 bg-charcoal/40 p-7">
                <h3 className="font-serif text-xl italic text-brass">What happens next?</h3>
                <p className="mt-3 text-sm leading-relaxed text-ivory/65">
                  Once confirmed, you'll receive invitations to upcoming games by email. See one you
                  like? Book your place. We'll send confirmed Players the private venue details
                  shortly before the gathering. Then all that's left is to turn up on time and see
                  who else has been dealt in.
                </p>
                <p className="mt-3 text-xs leading-relaxed text-ivory/45">
                  Gmail users: if you don't see your confirmation or invitation, check Promotions
                  and Spam.
                </p>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <AnimatePresence mode="wait">
              {state === "done" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  data-testid="deal-me-in-success"
                  className="rounded-2xl border border-brass/25 bg-charcoal/60 p-10 text-left"
                >
                  <CheckCircle2 size={36} className="text-brass" aria-hidden="true" />
                  <h2 className="mt-5 font-serif text-3xl text-ivory">
                    You're in the <span className="italic text-brass">deck.</span>
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-ivory/70">
                    Your application has been received. Keep an eye on your inbox — and if you're a
                    Gmail user, check Promotions and Spam. Invitations to upcoming gatherings go to
                    confirmed Players first.
                  </p>
                  <Link
                    to="/lounges"
                    data-testid="success-view-lounges-link"
                    className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-brass transition-colors hover:text-ivory"
                  >
                    See upcoming Lounges
                  </Link>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  data-testid="deal-me-in-form"
                  className="space-y-6 rounded-2xl border border-brass/15 bg-charcoal/50 p-7 sm:p-10"
                  exit={{ opacity: 0, y: -12 }}
                >
                  <div>
                    <label htmlFor="dmi-first-name" className={labelCls}>
                      First name <span className="text-burgundy">*</span>
                    </label>
                    <input
                      id="dmi-first-name"
                      data-testid="dmi-first-name-input"
                      type="text"
                      autoComplete="given-name"
                      value={form.first_name}
                      onChange={set("first_name")}
                      className={inputCls}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="dmi-email" className={labelCls}>
                      Email <span className="text-burgundy">*</span>
                    </label>
                    <input
                      id="dmi-email"
                      data-testid="dmi-email-input"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={set("email")}
                      className={inputCls}
                      required
                    />
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="dmi-mobile" className={labelCls}>
                        Mobile <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="dmi-mobile"
                        data-testid="dmi-mobile-input"
                        type="tel"
                        autoComplete="tel"
                        placeholder="+61…"
                        value={form.mobile}
                        onChange={set("mobile")}
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="dmi-age" className={labelCls}>
                        Age <span className="text-burgundy">*</span>
                      </label>
                      <input
                        id="dmi-age"
                        data-testid="dmi-age-input"
                        type="number"
                        min="18"
                        max="120"
                        value={form.age}
                        onChange={set("age")}
                        className={inputCls}
                        required
                      />
                      <p className="mt-1.5 text-xs text-ivory/45">Players Lounge is strictly 18+.</p>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="dmi-position" className={labelCls}>
                      What position do you generally prefer?{" "}
                      <span className="font-normal text-ivory/45">(optional)</span>
                    </label>
                    <select
                      id="dmi-position"
                      data-testid="dmi-position-select"
                      value={form.position}
                      onChange={set("position")}
                      className={`${inputCls} appearance-none`}
                    >
                      <option value="">Select your preference</option>
                      {POSITIONS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs leading-relaxed text-ivory/45">
                      We ask about preferences to help create a balanced room. Your answers are
                      confidential and aren't shared with other Players.
                    </p>
                  </div>
                  <label
                    htmlFor="dmi-privacy"
                    className="flex cursor-pointer items-start gap-3 rounded-xl border border-brass/15 bg-ink/50 p-4"
                  >
                    <input
                      id="dmi-privacy"
                      data-testid="dmi-privacy-checkbox"
                      type="checkbox"
                      checked={form.privacy_ack}
                      onChange={set("privacy_ack")}
                      className="mt-1 h-5 w-5 shrink-0 accent-[#C49A4A]"
                    />
                    <span className="text-sm leading-relaxed text-ivory/70">
                      I understand my details are confidential, used only to communicate with me and
                      balance gatherings, and never shared. I have read the{" "}
                      <Link to="/privacy" className="text-brass underline underline-offset-2" data-testid="dmi-privacy-link">
                        privacy note
                      </Link>{" "}
                      and the{" "}
                      <Link to="/terms" className="text-brass underline underline-offset-2" data-testid="dmi-terms-link">
                        Terms &amp; Conditions
                      </Link>
                      .
                    </span>
                  </label>
                  {error && (
                    <p role="alert" data-testid="dmi-error" className="rounded-lg bg-burgundy/20 px-4 py-3 text-sm text-ivory">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="dmi-submit-button"
                    disabled={state === "sending"}
                    className="w-full rounded-xl bg-burgundy py-4 text-base font-semibold text-ivory transition-all duration-200 hover:bg-[#a33847] hover:shadow-[0_0_30px_rgba(140,46,59,0.5)] disabled:opacity-60"
                  >
                    {state === "sending" ? "Dealing you in…" : "Deal Me In"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </FadeUp>
        </div>
      </section>

      {/* TOWEL MERCHANDISE CUE */}
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <FadeUp>
          <div
            data-testid="towel-merch-panel"
            className="grid items-center gap-10 overflow-hidden rounded-2xl border border-brass/15 bg-charcoal/50 p-8 sm:p-12 md:grid-cols-2"
          >
            <div className="relative flex justify-center rounded-2xl bg-[radial-gradient(ellipse_at_center,rgba(196,154,74,0.14),transparent_65%)] p-8">
              <img
                src="/assets/players-hand-towel.png"
                alt="Players Lounge heavyweight black cotton hand towel with embroidered PL monogram"
                loading="lazy"
                className="max-h-72 w-auto rounded-lg object-contain drop-shadow-[0_24px_40px_rgba(0,0,0,0.6)]"
              />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-brass">
                <span aria-hidden="true" className="mr-2">♣</span>Lounge merchandise
              </p>
              <h2 className="mt-3 font-serif text-2xl text-ivory sm:text-3xl">
                Take a piece of the Lounge <span className="italic text-brass">with you.</span>
              </h2>
              <p className="mt-4 max-w-md text-[15px] leading-relaxed text-ivory/65">
                Add a Players Lounge hand towel when you book your event ticket. Collect it at the
                party.
              </p>
              <p className="mt-5 inline-block rounded-lg border border-brass/20 px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-ivory/50">
                Available as an event-ticket add-on
              </p>
            </div>
          </div>
        </FadeUp>
      </section>
    </div>
  );
};

export default DealMeIn;
